import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Divider, Tooltip } from 'antd';
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  HistoryOutlined,
  SettingOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  CustomButton,
  CustomTable,
  CustomTabs,
  CustomText,
  CustomTextArea,
  FormInput,
  FormSelect,
  useCustomToast,
} from '../../../../components';
import { TechnicalRequest } from '../../types/technical-concept';
import ModalManageItem from '../components/ByRoles/ModalManageItem';
import ModalAddItemRolActivo from '../components/ByRoles/ModalAddItemRolActivo';
import { Asset } from '../components/ModalAddItemActivo';
import { TechnicalConceptAPI } from '../../api/technicalConceptAPI';
import { useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { authAtom } from '../../../../state';

interface TechnicalConceptDetailViewProps {
  detailData?: TechnicalRequest;
  setFooterLeft: (footer: React.ReactNode) => void;
  onDocuments: (id: string) => void;
  receiptPdf: (id: string) => void;
  formikSolicitud?: any;
  formikReturnPetition: any;
  formikApprovePetition?: any;
  isViewApproved?: boolean;
  refetchId?: () => void;
  refetch?: () => void;
}

const TechnicalConceptDetailView: React.FC<TechnicalConceptDetailViewProps> = ({
  detailData,
  setFooterLeft,
  onDocuments,
  receiptPdf,
  formikSolicitud,
  formikReturnPetition,
  formikApprovePetition,
  isViewApproved,
  refetchId,
  refetch,
}) => {
  const { showToast } = useCustomToast();
  const [isRoleTechnician, setIsRoleTechnician] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Asset | null>(null);
  const [isModalGestionActivo, setIsModalGestionActivo] = useState(false);
  const [status, setStatus] = useState<{ id: string; name: string }[]>([]);
  const [authData] = useAtom(authAtom);

  const location = useLocation();
  const isFromApprovals = location.pathname === '/approvals-technical-concepts';

  const currentUserString = getCurrentUserString();
  const isNotRequester = authData?.superadmin
    ? true
    : detailData?.requester !== currentUserString;
  const hiddenStatusIds = ['7', '10', '11'];
  const canManage = isViewApproved
    && !hiddenStatusIds.includes(String(detailData?.status_id || ''))
    && isNotRequester;

  const noDocuments = !detailData?.documents?.length

  const doNotShowClassLeader = !['11', '67'].includes(String(detailData?.status_id ?? ''))

  const generateReceiptAllowed = ['7'].includes(String(detailData?.status_id || ''))

  // FOOTER
  React.useEffect(() => {
    setFooterLeft(
      <>
        <CustomButton
          label="Descargar documentos"
          onClick={() => {
            onDocuments(detailData?.id || '');
          }}
          icon={<DownloadOutlined />}
          size="middle"
          buttonType="secondary"
          disabled={noDocuments}
        />

        <CustomButton
          style={{ marginLeft: '20px' }}
          label="Generar comprobante"
          disabled={!generateReceiptAllowed}
          onClick={() => {
            if (detailData?.id) {
              receiptPdf(detailData.id || '');
            }
          }}
          icon={<DownloadOutlined />}
          size="middle"
          buttonType="secondary"
        />
      </>
    );
  }, [detailData, setFooterLeft]);

  function getCurrentUserString() {
    if (!authData) return '';

    try {
      const { document, name, last_name } = authData?.user
      return `${document} - ${name} ${last_name}`;
    } catch (error) {
      throw error;
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await TechnicalConceptAPI.fetchParameters(
          ['concept_status'],
        );
        if (data) setStatus(data.concept_status.map((item: any) => ({
          id: String(item.id),
          name: item.status,
        })) || []);
      } catch (error) {
        console.error('Error fetching parameters:', error);
      }
    };
    fetchData();
  }, [detailData]);

  useEffect(() => {
    const storedToken = localStorage.getItem('auth');

    if (storedToken) {
      try {
        const parsedToken = JSON.parse(storedToken);
        const role = parsedToken?.role?.toLowerCase();
        if (role === 'Tecnico' && !isRoleTechnician) {
          setIsRoleTechnician(true);
        } else if (role !== 'Tecnico' && isRoleTechnician) {
          setIsRoleTechnician(false);
        }
      } catch (error) {
        console.error('Error al parsear el token:', error);
        setIsRoleTechnician(false);
      }
    } else {
      if (isRoleTechnician) {
        setIsRoleTechnician(false);
      }
    }
  }, [isRoleTechnician]);

  const handleManageItem = (item: any) => {
    if (detailData?.status_id === '67' && (!formikSolicitud.values.status_id || !formikSolicitud.values.reason)) {
      showToast('error', 'Por favor, complete el estado y las observaciones antes de gestionar el activo.');
      return;
    }

    if (!item.id) {
      console.error("El registro no tiene un itemable_id válido:", item);
      return;
    }
    setSelectedItem({
      ...item,
      id: item.id,
      description: item.description || "Sin descripción",
      plate_code: item.plate_code || "",
      responsible: item.responsible,
      asset_group: item.asset_group,
      asset_class: item.asset_class,
      item: item.item,
      status: item.status,
      vehicle: item.vehicle,
      asset_class_id: item.asset_class_id,
      evaluations: item.evaluations || undefined,
      ticket: item.ticket || '',
      asset_items: item.asset_items,
    });

    setIsModalGestionActivo(true);
  };

  const handleViewItem = (record: any) => {
    if (!record.id) {
      console.error("El registro no tiene un ID válido:", record);
      return;
    }

    setSelectedItem(record);
    setIsModalOpen(true);
  };

  const historyColumns = [
    {
      title: 'Item',
      dataIndex: 'index',
      key: 'index',
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Fecha',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Realizado por',
      dataIndex: 'user',
      key: 'user',
      render: (_: any, record: any) =>
        record.user_document && record.user
          ? `${record.user_document} - ${record.user}`
          : 'N/A',
    },
    {
      title: 'Observaciones',
      dataIndex: 'observations',
      key: 'observations',
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
    },
  ];


  const documentColumns = [
    {
      title: 'Item',
      dataIndex: 'index',
      key: 'index',
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
  ];

  const isShowStatus = ['67', '68', '10'].includes(detailData?.status_id ?? '') && isNotRequester;
  const isDisabledStatus = detailData?.status_id === '68' || detailData?.status_id === '10';

  return (
    <>
      {isShowStatus && (
        <Row gutter={[16, 16]}>
          {isFromApprovals && (
            <>
              <Col xs={24} sm={24} md={12} lg={12}>
                <FormSelect
                  label="Estado de la solicitud"
                  name="status_id_caracteristica"
                  placeholder="Seleccione"
                  value={detailData?.manager ? detailData?.status_id : formikSolicitud.values.status_id}
                  onChange={(value) => formikSolicitud.setFieldValue('status_id', value)}
                  options={status}
                  onBlur={() => formikSolicitud.setFieldTouched("status_id", true)}
                  disabled={isDisabledStatus}
                />
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <FormInput
                  label="Lider de clase:"
                  name="manage"
                  value={detailData?.manager || 'Sin registrar'}
                  disabled
                />
              </Col>

              <Col xs={24} sm={24} md={24} lg={24}>
                <CustomTextArea
                  label="Observaciones"
                  name="reason"
                  placeholder="Seleccione"
                  value={detailData?.manager ? detailData?.observations : formikSolicitud.values.reason}
                  error={formikSolicitud.errors.reason}
                  touched={formikSolicitud.touched.reason}
                  onChange={formikSolicitud.handleChange}
                  onBlur={formikSolicitud.handleBlur}
                  disabled={isDisabledStatus}
                />
              </Col>
            </>
          )}
        </Row>
      )}

      {/* Información General */}
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <CustomText text="Solicitante:" strong fontSize="small" />
          <CustomText text={detailData?.requester} fontSize="middle" />
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <CustomText text="Fecha solicitud:" strong fontSize="small" />
          <CustomText text={detailData?.query_date} fontSize="middle" />
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <CustomText text="Estado solicitud:" strong fontSize="small" />
          <CustomText text={detailData?.status} fontSize="middle" />
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <CustomText text="Tipo de bien:" strong fontSize="small" />
          <CustomText text={detailData?.goods_type} fontSize="middle" />
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <CustomText text="Dependencia:" strong fontSize="small" />
          <CustomText text={detailData?.dependency} fontSize="middle" />
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <CustomText text="Ubicación:" strong fontSize="small" />
          <CustomText text={detailData?.location} fontSize="middle" />
        </Col>
        {doNotShowClassLeader && (
          <Col xs={24} sm={12} md={8} lg={6}>
            <CustomText text="Líder de clase:" strong fontSize="small" />
            <CustomText text={detailData?.manager ?? 'Sin registro'} fontSize="middle" />
          </Col>
        )}
      </Row>

      <Row>
        <Col xs={24} sm={12} md={8} lg={8}>
          <CustomText text="Observaciones:" strong fontSize="small" />
          <CustomText text={detailData?.observations} fontSize="middle" />
        </Col>
      </Row>

      <Divider />

      <CustomText text="Activo" strong fontSize="middle" />

      <CustomTabs
        defaultActiveKey="1"
        items={[
          {
            key: '1',
            label:
              <span>
                <UnorderedListOutlined style={{ marginRight: 4 }} />
                Item
              </span>,
            children: (
              <CustomTable
                columns={[
                  { title: 'Ítem', dataIndex: 'id', key: 'Item' },
                  { title: 'Código/placa', dataIndex: 'plate_code', key: 'plate_code' },
                  { title: 'Descripción', dataIndex: 'description', key: 'description' },
                  {
                    title: 'Clase de activo',
                    key: 'asset_class',
                    dataIndex: 'asset_class',
                    render: (assetClass: { code: string; name: string }) => {
                      return assetClass ? `${assetClass.code} - ${assetClass.name}` : 'Sin clase';
                    },
                  },
                  {
                    title: 'Artículo',
                    key: 'item',
                    dataIndex: 'item',
                    render: (item: { code: string; name: string }) => {
                      return item ? `${item.code} - ${item.name}` : 'Sin clase';
                    },
                  },
                  { title: 'Adiciones', dataIndex: 'assets_count', key: 'assets_count' },
                  { title: 'Estado del bien', dataIndex: 'good_status', key: 'good_status' },
                  {
                    title: 'Acciones',
                    key: 'actions',
                    render: (_, record) => (
                      <>
                        <Tooltip title="Ver activo">
                          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewItem(record)} />
                        </Tooltip>

                        {canManage && (
                          <Tooltip title="Gestionar activo">
                            <Button type="link" icon={<SettingOutlined />} onClick={() => handleManageItem(record)} />
                          </Tooltip>
                        )}
                      </>
                    ),
                  },
                ]}
                dataSource={detailData?.items || []}
                pagination={false}
              />
            ),
          },
          {
            key: '2',
            label:
              <span>
                <FileTextOutlined style={{ marginRight: 4 }} />
                Documentos
              </span>,
            children: (
              <CustomTable
                dataSource={detailData?.documents?.map((doc, index) => ({
                  key: index,
                  createdAt: doc.created_at,
                  name: doc.url?.split('/').pop(),
                  link: doc.url,
                }))}
                columns={documentColumns}
                pagination={false}
              />
            ),
          },
          {
            key: '3',
            label:
              <span>
                <HistoryOutlined style={{ marginRight: 4 }} />
                Historial de la solicitud
              </span>,
            children: <CustomTable dataSource={detailData?.history} columns={historyColumns} pagination={false} />,
          },
        ]}
      />

      {isModalOpen && (
        <ModalAddItemRolActivo
          selectedItem={selectedItem}
          isModalAddItem={isModalOpen}
          setIsModalAddItem={setIsModalOpen}
          formik={formikSolicitud}
          onAddItem={() => { }}
        />
      )}

      {isModalGestionActivo && (
        <ModalManageItem
          title={`Detalle solicitud de conceptos técnicos - ${detailData?.ticket || ""}`}
          isModalManageItem={isModalGestionActivo}
          setIsModalManageItem={setIsModalGestionActivo}
          selectedItem={selectedItem}
          formik={formikSolicitud}
          status_id={detailData?.status_id || ""}
          formikReturnPetition={formikReturnPetition}
          formikApprovePetition={formikApprovePetition}
          detailData={detailData?.items || []}
          refetchId={refetchId}
          refetch={refetch}
        />
      )}
    </>
  );
};

export default TechnicalConceptDetailView;
